import { collectionGroup, doc, documentId, getDocs, query, setDoc, where } from 'firebase/firestore';
import { db } from 'shared/services/firebase';
import { subscribeToDoc, getDocOnce } from 'shared/services/firestore';
import { mapPrivateInfo, PrivateInfo } from 'domains/experts/model/privateInfo';

/** aligned's `_userAccountPrivateRef`: a fixed `info` doc under the `private` subcollection. */
function privateInfoRef(userAccountId: string) {
  return doc(db, 'user_accounts', userAccountId, 'private', 'info');
}

export function getPrivateInfo(userAccountId: string): Promise<PrivateInfo | null> {
  return getDocOnce(privateInfoRef(userAccountId), mapPrivateInfo);
}

const PRIVATE_INFO_READ_BATCH_SIZE = 24;

/**
 * Loads private info for a roster without opening one Firestore request per
 * expert at once. Private info lives in a subcollection, so it cannot use the
 * same document-id `in` query as top-level user accounts.
 */
export async function getPrivateInfoByUserAccountIds(
  userAccountIds: string[],
): Promise<Record<string, PrivateInfo | null>> {
  const uniqueIds = Array.from(new Set(userAccountIds.filter(Boolean)));
  const result: Record<string, PrivateInfo | null> = {};
  for (let offset = 0; offset < uniqueIds.length; offset += PRIVATE_INFO_READ_BATCH_SIZE) {
    const batch = uniqueIds.slice(offset, offset + PRIVATE_INFO_READ_BATCH_SIZE);
    const entries = await Promise.all(
      batch.map(async (userAccountId) => [
        userAccountId,
        await getPrivateInfo(userAccountId).catch(() => null),
      ] as const),
    );
    for (const [userAccountId, info] of entries) result[userAccountId] = info;
  }
  return result;
}

const PRIVATE_INFO_PATH_QUERY_SIZE = 30;

/**
 * Loads private/info for a large roster with collection-group queries filtered
 * by document path, 30 accounts per query, instead of one read per account —
 * thousands of single reads at once overrun the client's connection
 * ("Too many outstanding requests"); a few dozen batched queries at once do
 * not. Firestore checks a path-filtered query
 * against each named document's own rule, so this returns exactly what
 * per-document reads would (pinned by aligned's
 * test/firestore_rules/private_collection_group.rules.test.mjs). A missing doc
 * is null. If a query fails, its accounts are read one by one, a failed read
 * also being null, as `getPrivateInfoByUserAccountIds` does.
 */
export async function getPrivateInfoForManyUserAccounts(
  userAccountIds: string[],
): Promise<Record<string, PrivateInfo | null>> {
  const uniqueIds = Array.from(new Set(userAccountIds.filter((id) => id && !id.includes('/'))));
  const result: Record<string, PrivateInfo | null> = {};
  const chunks: string[][] = [];
  for (let offset = 0; offset < uniqueIds.length; offset += PRIVATE_INFO_PATH_QUERY_SIZE) {
    chunks.push(uniqueIds.slice(offset, offset + PRIVATE_INFO_PATH_QUERY_SIZE));
  }
  const loadChunk = async (chunk: string[]): Promise<void> => {
    try {
      const snap = await getDocs(
        query(collectionGroup(db, 'private'), where(documentId(), 'in', chunk.map((id) => privateInfoRef(id)))),
      );
      // Identity is the document path: the owning account is the doc's grandparent.
      const byAccount = new Map(snap.docs.map((d) => [d.ref.parent.parent?.id ?? '', mapPrivateInfo(d.id, d.data())]));
      for (const id of chunk) result[id] = byAccount.get(id) ?? null;
    } catch {
      const infos = await Promise.all(chunk.map((id) => getPrivateInfo(id).catch(() => null)));
      chunk.forEach((id, index) => { result[id] = infos[index]; });
    }
  };
  await Promise.all(chunks.map(loadChunk));
  return result;
}

export function subscribeToPrivateInfo(
  userAccountId: string,
  onNext: (info: PrivateInfo | null) => void,
  onError?: (err: Error) => void,
) {
  return subscribeToDoc(privateInfoRef(userAccountId), mapPrivateInfo, onNext, {
    context: 'subscribeToPrivateInfo',
    onError,
  });
}

/**
 * Partial merge-write, matching aligned's `updateUserAccountPrivateFields`
 * (`set(..., {merge: true})` — never a full-document replace). Every Settings
 * screen writes PII this way, one or two fields at a time.
 */
export function updatePrivateInfoFields(
  userAccountId: string,
  fields: Partial<PrivateInfo>,
): Promise<void> {
  return setDoc(privateInfoRef(userAccountId), fields, { merge: true });
}
